import DateRange from '@/components/date/DateRange';
import AsOfDatePicker from '@/components/date/AsOfDatePicker';
import { mobilePrimaryDatePickerButtonSx } from '@/components/date/sharedDatePickerSx';
import { mobileWidth } from '@/constants';
import { DataRangeFilterEnum } from '@/constants/dates';
import FilterListOutlinedIcon from '@mui/icons-material/FilterListOutlined';
import Badge from '@mui/material/Badge';
import { Box, Button, useMediaQuery } from '@mui/material';
import { useLocalization } from '@/hooks/useLocalization';

interface PageHeaderMenuProps {
  startDate?: string;
  endDate?: string;
  showDatePicker?: boolean;
  toggleDateRange?: () => void;
  handleDateRangeChange?: (
    startDate: string,
    endDate: string,
    shortcutLabel?: string,
    shortcutFilter?: DataRangeFilterEnum,
  ) => void;
  showFilter?: boolean;
  onFilterClick?: () => void;
  useSingleDate?: boolean;
  filterButtonText?: string;
  customFilterIcon?: React.ReactNode;
  filterCount?: number;
}

export const PageHeaderMenu: React.FC<PageHeaderMenuProps> = ({
  startDate,
  endDate,
  toggleDateRange,
  handleDateRangeChange = () => {},
  showFilter = false,
  onFilterClick = () => {},
  useSingleDate = false,
  showDatePicker = false,
  filterButtonText = 'Filter',
  customFilterIcon,
  filterCount,
}) => {
  const { t } = useLocalization();
  const isMobileMatch = useMediaQuery(mobileWidth);
  const shouldShowDatePicker = showDatePicker;
  const filterIcon = customFilterIcon || <FilterListOutlinedIcon />;
  const handleSingleDateChange = (date: string) => {
    handleDateRangeChange('', date);
    if (isMobileMatch && toggleDateRange) toggleDateRange();
  };

  if (isMobileMatch) {
    return (
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          alignItems: 'center',
          justifyContent: 'flex-end',
        }}
      >
        {showFilter && filterCount && filterCount > 0 ? (
          <Badge
            badgeContent={filterCount > 9 ? '9+' : filterCount}
            color='primary'
          >
            <Button
              variant='outlined'
              color='primary'
              size='large'
              type='button'
              onClick={onFilterClick}
              aria-label={t('pageHeader.openFilters')}
              sx={mobilePrimaryDatePickerButtonSx}
            >
              {filterIcon}
            </Button>
          </Badge>
        ) : (
          showFilter && (
            <Button
              variant='outlined'
              color='primary'
              size='large'
              type='button'
              onClick={onFilterClick}
              aria-label={t('pageHeader.openFilters')}
              sx={mobilePrimaryDatePickerButtonSx}
            >
              {filterIcon}
            </Button>
          )
        )}
        {shouldShowDatePicker ? (
          useSingleDate ? (
            <AsOfDatePicker
              date={endDate}
              onChange={handleSingleDateChange}
              useMobilePrimaryIconButton
            />
          ) : (
            <DateRange
              startDate={startDate}
              endDate={endDate}
              onChange={handleDateRangeChange}
              useMobilePrimaryIconButton
            />
          )
        ) : null}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 2,
        alignItems: 'center',
      }}
    >
      {shouldShowDatePicker ? (
        <>
          {useSingleDate ? (
            <AsOfDatePicker
              date={endDate}
              onChange={handleSingleDateChange}
            />
          ) : (
            <DateRange
              startDate={startDate}
              endDate={endDate}
              onChange={handleDateRangeChange}
            />
          )}
        </>
      ) : null}
      {showFilter && filterCount && filterCount > 0 ? (
        <Badge
          badgeContent={filterCount > 9 ? '9+' : filterCount}
          color='primary'
          sx={{
            height: '100%',
          }}
        >
          <Button
            size='large'
            startIcon={filterIcon}
            variant='outlined'
            onClick={onFilterClick}
            sx={{
              height: '100%',
            }}
          >
            {filterButtonText}
          </Button>
        </Badge>
      ) : (
        showFilter && (
          <Button
            size='large'
            startIcon={filterIcon}
            variant='outlined'
            onClick={onFilterClick}
            sx={{
              height: '100%',
              root: {
                height: '100%',
              },
            }}
          >
            {filterButtonText}
          </Button>
        )
      )}
    </Box>
  );
};
